import { memo, useContext, useState } from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import findLast from "lodash/findLast";
import takeRight from "lodash/takeRight";
import { parse } from "qs";
import { FormattedMessage, FormattedPlural, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import ProductNameWithCategory from "app/pages/.shared/ProductNameWithCategory/ProductNameWithCategory";
import NegociatedItem from "app/pages/FicheProduit/NegociatedList/NegociatedItem";
import EmiratesPassNegociatedItem from "app/pages/FicheProduit/NegociatedList/EmiratesPassNegociatedItem";
import {
	BRANDS,
	DESTINATION_LEVELS,
	NEGOCIATED_TYPES,
	TOUR_PRODUCT_TYPES,
	ICON_SIZES,
	PRODUCT_TAG_CODES,
} from "app/constants";
import FlightDetailModal from "app/pages/SmartDP/Listing/FlightDetailModal/FlightDetailModal";
import List from "app/pages/.shared/List";
import ProductRatingContainer from "app/pages/FicheProduit/components/ProductRating/ProductRatingContainer";
import SdpProductPricingBlock from "app/pages/SmartDP/Listing/SmartDPProduct/SdpProductPricingBlock/SdpProductPricingBlock";
import { shouldShowFavoriteBadge, isBrandWithAccount } from "app/utils/utils";
import { getIsDubaiProduct, getUnbeatableOffer } from "app/utils/productUtils";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { FlightBaggageBlock } from "app/features/luggage";
import {
	HotelCard,
	HotelCardDetail,
	HotelCardDetailList,
} from "app/features/product/components/HotelCard/HotelCard";
import AppGlobalsContext from "app/AppGlobalsContext";
import { BadgeItem } from "app/components/ui/badge/BadgeItem";
import { Amount } from "app/components/ui/Amount/Amount";

const getUnbeatableOfferType = ({ unbeatableOfferTag, shouldDisplayMemberOnlyTag }) => {
	if (shouldDisplayMemberOnlyTag) {
		return "membersOnly";
	}

	if (unbeatableOfferTag === PRODUCT_TAG_CODES.LIMITED_TIME) {
		return "limited";
	} else {
		return "lastDays";
	}
};

const getLocationLabel = geography =>
	takeRight(
		geography?.levels?.filter(geo => geo.label !== DESTINATION_LEVELS.OTHER),
		2
	)
		?.reverse()
		?.map(geo => geo.label)
		?.join(", ") || "";

const SmartDPProduct = ({
	accommodation = {},
	onClick = () => {},
	isSpotlight,
	position, // for analytics
	isStopOver = false,
	showBadgeSecondaryText = true,
	orientation,
	photoSizes,
	subgrid = false,
	className,
}) => {
	const { search } = useLocation();
	const { formatMessage } = useIntl();
	const queryParams = parse(search, { ignoreQueryPrefix: true });
	const [isFlightDetailOpened, toggleFlightDetail] = useState(false);
	const { track } = useAnalytics();
	const { brand, isUserConnected, strategy } = useContext(AppGlobalsContext);
	const isDubaiProduct = getIsDubaiProduct(accommodation?.geography);
	const showEmiratesPassItem = brand === BRANDS.EK && isDubaiProduct;
	const showDiscountedAccommodation = accommodation?.discountBadgeVisibility ?? false;

	const showFlightDetail = () => {
		track("sdp-product-view-flight-detail-click");
		toggleFlightDetail(true);
	};

	const hideFlightDetail = () => {
		toggleFlightDetail(false);
	};

	const handleSelect = () => {
		onClick(accommodation, position);
	};

	const flightOptions = accommodation.flightDetails?.flightOptions;

	const isBaggageFree = Boolean(
		findLast(
			flightOptions?.flightBagageOptions,
			option =>
				flightOptions?.flightBagageOption?.canBeBooked &&
				flightOptions?.flightBagageOption?.included === false &&
				option?.pricePerBooking === 0
		)
	);

	let negotiatedList = [];

	// accommodation.accommodationLabel, accommodation.boardLabel, accommodation.total are set on getSDPProducts selector
	negotiatedList.push(
		{
			label: accommodation.accommodationLabel,
			type: NEGOCIATED_TYPES.ACCOMMODATION,
		},
		{ label: accommodation.boardLabel, type: NEGOCIATED_TYPES.BOARD }
	);

	if (get(accommodation, "negotiated.length") > 0) {
		const labels = accommodation.negotiated.map((nego, index) => {
			if (index > 0) {
				return nego.label.toLowerCase();
			}
			return nego.label;
		});
		negotiatedList = negotiatedList.concat({
			label: labels.join(", "),
		});
	}

	const tripAdvisor = accommodation.tripadvisor || {};
	const holidayCheck = accommodation.holidaycheck || {};
	const accommodationName = (
		<ProductNameWithCategory category={accommodation.category} name={accommodation.name} />
	);
	const accommodationNights = accommodation?.accommodationNights || queryParams?.duration;
	const isFavorite = shouldShowFavoriteBadge(accommodation);
	const unbeatableOfferTag = getUnbeatableOffer(accommodation);
	const isTour = TOUR_PRODUCT_TYPES.includes(accommodation?.productExperience);
	const discountValue = accommodation?.discountValue;
	const discountedPercentage = accommodation?.discountPercentage;
	const totalPrice = Number(accommodation?.total) || 0;
	const publicPrice = discountValue > 0 ? totalPrice + discountValue : 0;
	const photos = (accommodation.photos ?? [])
		.filter(photo => photo?.url)
		.map(photo => ({ url: photo.url, thumbnail: photo.thumbnail }));

	const shouldDisplayMemberOnlyTag = isBrandWithAccount({ brand, strategy }) && !isUserConnected;

	const unbeatableOffer = getUnbeatableOfferType({
		unbeatableOfferTag,
		shouldDisplayMemberOnlyTag,
	});

	const hasBadges =
		unbeatableOfferTag?.length > 0 ||
		isFavorite ||
		isTour ||
		isBaggageFree ||
		showDiscountedAccommodation;

	const badges = hasBadges && (
		<>
			{unbeatableOfferTag && (
				<BadgeItem
					type={unbeatableOffer}
					showSecondaryText={showBadgeSecondaryText}
					data-testid="sdp-product-badge"
				/>
			)}
			{isFavorite && !unbeatableOfferTag && (
				<BadgeItem type="topPicks" data-testid="sdp-product-badge" />
			)}
			{isTour && <BadgeItem type="tours" data-testid="sdp-product-badge" />}
			{showDiscountedAccommodation && (
				<BadgeItem
					type="discount"
					data-testid="discounted-accommodation-tag"
					primaryValues={{
						amount: <Amount value={discountValue} size="small" />,
					}}
				/>
			)}
			{isBaggageFree && <BadgeItem type="luggage" data-testid="sdp-product-badge" />}
		</>
	);

	const negotiatedItems = negotiatedList.map((negociated, index) => (
		<NegociatedItem
			key={index}
			sizeIcon={ICON_SIZES.SM}
			type={negociated.type}
			label={negociated.label}
			enableLineClamp={Boolean(
				!negociated.type || negociated.type === NEGOCIATED_TYPES.OTHER
			)}
			lineClampFullText={
				<List
					enableHTMLFormatting={true}
					items={get(accommodation, "negotiated", []).map(nego => nego.label)}
				/>
			}
		/>
	));

	if (showEmiratesPassItem) {
		negotiatedItems.push(
			<EmiratesPassNegociatedItem key="emirates-pass" sizeIcon={ICON_SIZES.SM} />
		);
	}

	const duration = accommodationNights > 0 && (
		<FormattedPlural
			value={accommodationNights}
			one={
				<FormattedMessage
					id="general.duration.singular"
					values={{ duration: accommodationNights }}
				/>
			}
			other={
				<FormattedMessage
					id="general.duration.plural"
					values={{ duration: accommodationNights }}
				/>
			}
		/>
	);

	return (
		<>
			<HotelCard
				name={accommodation.name}
				category={accommodation.category}
				onSelect={handleSelect}
				ctaLabel={
					<FormattedMessage
						id={isStopOver ? "stop.over.product.cta.label" : "sdp.product.cta.label"}
					/>
				}
				photos={photos}
				photosLabel={formatMessage(
					{ id: "sdp.product.photos.label" },
					{ name: accommodation.name }
				)}
				photoSizes={photoSizes}
				badges={badges}
				rating={
					<ProductRatingContainer
						locationName={accommodationName}
						tripAdvisor={{
							id: tripAdvisor.id,
							rating: tripAdvisor.rating,
							reviewsCount: tripAdvisor.num_reviews,
						}}
						holidayCheck={{
							...holidayCheck,
							size: "small",
						}}
					/>
				}
				location={getLocationLabel(accommodation?.geography)}
				duration={duration}
				details={
					<HotelCardDetailList>
						{negotiatedItems.map(item => (
							<HotelCardDetail key={item.key}>{item}</HotelCardDetail>
						))}
					</HotelCardDetailList>
				}
				flight={
					<FlightBaggageBlock
						flight={accommodation.flightDetails}
						onViewDetail={!isStopOver ? showFlightDetail : undefined}
					/>
				}
				price={
					<SdpProductPricingBlock
						isStopOver={isStopOver}
						accommodation={accommodation}
						showDiscountedAccommodation={showDiscountedAccommodation}
						discountedPercentage={discountedPercentage}
						publicPrice={publicPrice}
						discountValue={discountValue}
						price={totalPrice}
					/>
				}
				spotlightLabel={
					isSpotlight && <FormattedMessage id="sdp.product.spotlight.title" />
				}
				orientation={orientation}
				subgrid={subgrid}
				className={className}
			/>

			<FlightDetailModal
				isOpened={isFlightDetailOpened}
				close={hideFlightDetail}
				flight={accommodation.flightDetails}
				isStopOver={isStopOver}
			/>
		</>
	);
};

SmartDPProduct.propTypes = {
	onClick: PropTypes.func,
	accommodation: PropTypes.object,
	position: PropTypes.number,
	isSpotlight: PropTypes.bool,
	isStopOver: PropTypes.bool,
	showBadgeSecondaryText: PropTypes.bool,
	orientation: PropTypes.oneOf(["vertical", "horizontal"]),
	photoSizes: PropTypes.string,
	subgrid: PropTypes.bool,
};

export default memo(SmartDPProduct);
